/**
 * <UserName user={u} /> — single source of truth for rendering usernames
 * with status-driven decoration (Donator + Moderator).
 *
 * Reads two flags on the user object:
 *   - is_donator    → neon-green name (#39FF14) + soft glow + 💚 suffix
 *   - is_moderator  → existing purple "MOD" pill
 *
 * Both can stack (mod + donator → green name + purple pill).
 *
 * Pass `to="/profile/:username"` to make the name a link. The component
 * honors `prefers-reduced-motion` and disables the pulse for users who
 * opt out.
 */
import { Link } from "react-router-dom";

const NEON = "#39FF14";

function donatorTextStyle() {
  return {
    color: NEON,
    textShadow: `0 0 8px rgba(57,255,20,0.55), 0 0 2px rgba(57,255,20,0.9)`,
    fontWeight: 700,
  };
}

export function UserName({ user, to, className = "", showBadges = true, hideModBadge = false, hideDonatorIcon = false }) {
  if (!user) return null;
  const name = user.username || user.display_name || user.name || "—";
  const isMod = !!user.is_moderator;
  const isDonator = !!user.is_donator;

  const nameNode = (
    <span
      data-testid={isDonator ? "username-donator" : "username"}
      data-donator={isDonator || undefined}
      style={isDonator ? donatorTextStyle() : undefined}
      className={`solo-username-text inline ${className}`}
    >
      {name}
    </span>
  );

  const inner = to ? (
    <Link to={to} className="hover:underline">
      {nameNode}
    </Link>
  ) : (
    nameNode
  );

  return (
    <span className="inline-flex items-center gap-1.5">
      {inner}
      {showBadges && isDonator && !hideDonatorIcon && (
        <span
          data-testid="badge-donator"
          aria-label="Donator"
          title="Donator — supports FootGuesser"
          className="text-[11px]"
        >
          💚
        </span>
      )}
      {showBadges && isMod && !hideModBadge && (
        <span
          data-testid="badge-mod"
          className="text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 rounded bg-purple-600/30 border border-purple-400/50 text-purple-200"
        >
          MOD
        </span>
      )}
    </span>
  );
}

export default UserName;
